import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  IconButton,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  SecurityOutlined,
  AddOutlined,
  SearchOutlined,
  FileDownloadOutlined,
  RefreshOutlined,
  EditOutlined,
  DeleteOutline,
  CheckCircleOutline,
  CancelOutlined,
  AccountBalanceWalletOutlined,
  NavigateNext,
  RuleOutlined,
  KeyboardArrowDownOutlined,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { tokens } from "../../../theme";
import {
  WALLET_APPROVAL_TYPES,
  getStoredSgwApprovals,
  saveStoredSgwApprovals,
} from "./approvalConstants";

import {
  fetchSgwApprovalsFromApi,
  assignSgwApprovalsApi,
  updateSgwApprovalApi,
  softDeleteSgwApprovalApi,
} from "../../../data/WalletService/walletGraphQL";
import SgwApprovalForm from "./SgwApprovalForm";

// ── Resolve current company ID from the decoded JWT ──────────────────────────
// SGW approvals are platform-wide but the localStorage cache is company-keyed
// so switching companies always triggers a fresh API fetch instead of silently
// serving a stale cross-company cache.
const resolveCompanyId = () => {
  try {
    const decoded = JSON.parse(localStorage.getItem("decodedToken") || "{}");
    const cid =
      decoded?.CompanyId ?? decoded?.companyId ??
      decoded?.company_id ?? decoded?.TenantId ?? decoded?.tenantId;
    const num = Number(cid);
    return Number.isFinite(num) && num > 0 ? num : null;
  } catch {
    return null;
  }
};

const SgwApprovals = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // Data & State
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterApprovalType, setFilterApprovalType] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL"); // ALL, Yes, No

  // Pagination for DataGrid
  const [pageSize, setPageSize] = useState(10);

  // Form Modal State
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Action Menu State (for Action button dropdown)
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedActionRow, setSelectedActionRow] = useState(null);

  // Current company — resolved fresh on each render from the JWT.
  // SGW approvals are platform-wide but the localStorage cache is company-keyed
  // so switching companies never serves stale data from the previous company.
  const currentCompanyId = resolveCompanyId();

  const handleOpenActionMenu = (event, row) => {
    event.stopPropagation();
    setActionAnchorEl(event.currentTarget);
    setSelectedActionRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionAnchorEl(null);
    setSelectedActionRow(null);
  };

  // Load approvals from live API.
  // Cache is keyed by company so switching companies always gets fresh data.
  // If the API returns an empty list the table will correctly show "No rows"
  // rather than silently serving another company's cached assignments.
  const loadApprovals = useCallback(async () => {
    setLoading(true);
    const cid = resolveCompanyId();
    try {
      const liveData = await fetchSgwApprovalsFromApi(false);
      if (Array.isArray(liveData) && liveData.length > 0) {
        setApprovals(liveData);
        saveStoredSgwApprovals(liveData, cid);
      } else {
        // API returned empty: show empty — do NOT fall back to another
        // company's cache. If we have a same-company cached copy, use it.
        const cached = getStoredSgwApprovals(cid);
        setApprovals(cached || []);
      }
    } catch (e) {
      console.warn("Could not fetch SGW approvals from live API, falling back to cache:", e);
      // Only fall back to this company's own cache slot
      const cached = getStoredSgwApprovals(cid);
      setApprovals(cached || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  // Handle Save / Submit from Form (persists via GraphQL mutation then updates state)
  const handleSaveApproval = async (incoming) => {
    const newItems = Array.isArray(incoming) ? incoming : [incoming];
    let currentList = [...approvals];

    newItems.forEach((newItem) => {
      const existingIndex = currentList.findIndex(
        (item) =>
          item.id === newItem.id ||
          (item.userId === newItem.userId &&
            item.assignedApproval === newItem.assignedApproval)
      );

      if (existingIndex >= 0) {
        currentList[existingIndex] = {
          ...currentList[existingIndex],
          ...newItem,
          isDeleted: false,
        };
      } else {
        currentList = [newItem, ...currentList];
      }
    });

    setApprovals(currentList);
    saveStoredSgwApprovals(currentList, currentCompanyId);

    // Persist to GraphQL backend endpoint
    try {
      if (newItems.length > 0) {
        const primary = newItems[0];
        await assignSgwApprovalsApi({
          userId: Number(primary.userId),
          approvalCodes: newItems.map((item) => item.assignedApproval),
          assignedBy: primary.assignedBy || "System Governance",
          notes: primary.notes || "",
        });
      }
    } catch (apiErr) {
      console.warn("SGW Approvals API mutation persisted locally; backend sync pending:", apiErr?.message);
    }
  };

  // Action Menu: Edit handler
  const handleEditFromAction = () => {
    if (!selectedActionRow) return;
    const rowToEdit = selectedActionRow;
    handleCloseActionMenu();
    setEditingItem(rowToEdit);
    setFormOpen(true);
  };

  // Action Menu: Soft Delete handler (executes softDeleteSgwApproval mutation)
  const handleSoftDeleteFromAction = () => {
    if (!selectedActionRow) return;
    const rowToDelete = selectedActionRow;
    handleCloseActionMenu();

    Swal.fire({
      title: "Delete Approval Permission?",
      html: `Are you sure you want to delete <b>${rowToDelete.assignedApprovalLabel}</b> authorization for <b>${rowToDelete.name}</b>?<br/><small style="color:#64748b">This record will be soft deleted and deactivated.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        let currentAdminName = "System Governance";
        try {
          const decoded = JSON.parse(localStorage.getItem("decodedToken") || "{}");
          currentAdminName = decoded.Name || decoded.Username || "System Governance";
        } catch (e) {}

        // Call backend GraphQL soft-delete mutation
        try {
          await softDeleteSgwApprovalApi(rowToDelete.id, currentAdminName);
        } catch (apiErr) {
          console.warn("Soft delete API error, applying local soft delete:", apiErr?.message);
        }

        // Soft delete locally: set isDeleted: true and isApproved: "No"
        const updated = approvals.map((row) =>
          row.id === rowToDelete.id
            ? {
                ...row,
                isDeleted: true,
                isApproved: "No",
                deletedAt: new Date().toISOString(),
              }
            : row
        );
        setApprovals(updated);
        saveStoredSgwApprovals(updated, resolveCompanyId());

        Swal.fire({
          icon: "success",
          title: "Soft Deleted!",
          text: "The approval permission has been soft deleted successfully.",
          timer: 1600,
          showConfirmButton: false,
        });
      }
    });
  };

  // Export to CSV
  const handleExportCSV = () => {
    const activeRecords = approvals.filter((a) => !a.isDeleted);
    if (!activeRecords.length) return;
    const headers = [
      "ID",
      "Name",
      "Username",
      "Email",
      "Phone Number",
      "Assigned Approval",
      "Assigned Approval (Yes/No)",
      "Assigned By",
      "Date",
      "Notes",
    ];

    const rows = activeRecords.map((a) => [
      `"${a.id}"`,
      `"${a.name}"`,
      `"${a.username}"`,
      `"${a.email}"`,
      `"${a.phone}"`,
      `"${a.assignedApprovalLabel}"`,
      `"${a.isApproved}"`,
      `"${a.assignedBy || ""}"`,
      `"${a.effectiveDate || ""}"`,
      `"${(a.notes || "").replace(/"/g, '""')}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `SGW_Approvals_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter & Search Logic (Filters out soft-deleted records)
  const filteredApprovals = useMemo(() => {
    return approvals.filter((item) => {
      // Exclude soft-deleted records from active grid
      if (item.isDeleted === true) return false;

      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        item.name.toLowerCase().includes(q) ||
        item.username.toLowerCase().includes(q) ||
        item.email.toLowerCase().includes(q) ||
        item.phone.toLowerCase().includes(q) ||
        item.assignedApprovalLabel.toLowerCase().includes(q);

      const matchesApproval =
        filterApprovalType === "ALL" ||
        item.assignedApproval === filterApprovalType;

      const matchesStatus =
        filterStatus === "ALL" || item.isApproved === filterStatus;

      return matchesSearch && matchesApproval && matchesStatus;
    });
  }, [approvals, searchQuery, filterApprovalType, filterStatus]);

  // KPI Metrics
  const stats = useMemo(() => {
    const activeRecords = approvals.filter((a) => !a.isDeleted);
    const total = activeRecords.length;
    const activeYes = activeRecords.filter((a) => a.isApproved === "Yes").length;
    const inactiveNo = activeRecords.filter((a) => a.isApproved === "No").length;
    const uniqueTypes = new Set(activeRecords.map((a) => a.assignedApproval)).size;
    return { total, activeYes, inactiveNo, uniqueTypes };
  }, [approvals]);

  // DataGrid Columns Definition
  const columns = useMemo(
    () => [
      {
        field: "rowIndex",
        headerName: "#",
        width: 60,
        sortable: false,
        renderCell: (params) => {
          const idx = filteredApprovals.findIndex((r) => r.id === params.row.id);
          return (
            <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 600 }}>
              {idx >= 0 ? idx + 1 : 1}
            </Typography>
          );
        },
      },
      {
        field: "name",
        headerName: "Name",
        width: 200,
        renderCell: (params) => (
          <Box sx={{ py: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: colors.grey[100], lineHeight: 1.2 }}
            >
              {params.row.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.grey[400], display: "block", mt: 0.2 }}
            >
              @{params.row.username}
            </Typography>
          </Box>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        width: 240,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: colors.grey[200] }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "phone",
        headerName: "Phone Number",
        width: 160,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "assignedApprovalLabel",
        headerName: "Assigned Approval",
        width: 290,
        renderCell: (params) => {
          const meta =
            WALLET_APPROVAL_TYPES.find(
              (a) => a.id === params.row.assignedApproval
            ) || {};
          return (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {params.value}
              </Typography>
              {meta.category && (
                <Chip
                  label={meta.category}
                  size="small"
                  sx={{
                    mt: 0.5,
                    height: 18,
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    backgroundColor: meta.bgLight || "rgba(0,0,0,0.06)",
                    color: meta.color || "#475569",
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "isApproved",
        headerName: "Assigned Approval (Yes / No)",
        width: 210,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const isYes = params.value === "Yes";
          return (
            <Chip
              icon={
                isYes ? (
                  <CheckCircleOutline sx={{ "&&": { color: "#ffffff" } }} />
                ) : (
                  <CancelOutlined sx={{ "&&": { color: "#ffffff" } }} />
                )
              }
              label={isYes ? "Yes (Authorized)" : "No (Revoked)"}
              size="small"
              sx={{
                fontWeight: 800,
                fontSize: "0.74rem",
                height: 24,
                backgroundColor: isYes ? "#16a34a" : "#dc2626",
                color: "#ffffff",
                boxShadow: isYes
                  ? "0 2px 8px rgba(22, 163, 74, 0.25)"
                  : "0 2px 8px rgba(220, 38, 38, 0.25)",
              }}
            />
          );
        },
      },
      {
        field: "assignedBy",
        headerName: "Assigned By",
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value || "Governance"}
          </Typography>
        ),
      },
      {
        field: "effectiveDate",
        headerName: "Date",
        width: 140,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ color: colors.grey[200] }}>
            {params.value || "—"}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 130,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            endIcon={<KeyboardArrowDownOutlined />}
            onClick={(e) => handleOpenActionMenu(e, params.row)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8rem",
              py: 0.4,
              px: 1.8,
              borderRadius: 1.5,
              borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
              color: colors.grey[100],
              "&:hover": {
                borderColor: "#1766a2",
                backgroundColor: "rgba(23, 102, 162, 0.08)",
              },
            }}
          >
            Action
          </Button>
        ),
      },
    ],
    [filteredApprovals, colors]
  );

  // Exact DataGrid styling matching WithdrawalApprovals table with inscroll
  const gridSx = {
    border: "none",
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: isDark ? colors.primary[500] : "#f1f5f9",
      fontWeight: 700,
      borderBottom: `1px solid ${
        isDark ? "rgba(255,255,255,0.08)" : "#cbd5e1"
      }`,
    },
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${
        isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9"
      }`,
    },
    "& .MuiDataGrid-virtualScroller": {
      "&::-webkit-scrollbar": {
        height: "12px",
        width: "12px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
        borderRadius: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: isDark ? "rgba(255,255,255,0.25)" : "#94a3b8",
        borderRadius: "6px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: isDark ? "rgba(255,255,255,0.35)" : "#64748b",
      },
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${
        isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
      }`,
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        p: "20px",
      }}
    >
      {/* ── Breadcrumbs & Header ────────────────────────────────────────── */}
      <Box sx={{ mb: 0.5 }}>
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 1, fontSize: "0.85rem" }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="/wallet-management"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <AccountBalanceWalletOutlined fontSize="inherit" />
            Wallet Management
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 600 }}>
            SGW Approvals
          </Typography>
        </Breadcrumbs>
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, color: colors.grey[100], letterSpacing: "-0.5px" }}
        >
          SGW Approval Permissions
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.grey[300], mt: 0.5, fontSize: "0.88rem" }}
        >
          Configure and govern Superuser (Role Group ID 1) approval authorizations for the Sababisha Global Wallet module
        </Typography>
      </Box>

      {/* ── Executive KPI Summary Cards ─────────────────────────────────── */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
              }`,
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 600 }}>
                  Total Superuser Approvers
                </Typography>
                <Box
                  sx={{
                    bgcolor: "rgba(23, 102, 162, 0.12)",
                    color: "#1766a2",
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SecurityOutlined fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                {stats.total}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Role Group ID 1 Only
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
              }`,
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 600 }}>
                  Active Approvals (Yes)
                </Typography>
                <Box
                  sx={{
                    bgcolor: "rgba(220, 252, 231, 0.6)",
                    color: "#16a34a",
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircleOutline fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#16a34a" }}>
                {stats.activeYes}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Authorized signing privileges
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
              }`,
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 600 }}>
                  Revoked / Inactive (No)
                </Typography>
                <Box
                  sx={{
                    bgcolor: "rgba(254, 226, 226, 0.6)",
                    color: "#dc2626",
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CancelOutlined fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#dc2626" }}>
                {stats.inactiveNo}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Permissions suspended
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
              }`,
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 600 }}>
                  Approval Types Active
                </Typography>
                <Box
                  sx={{
                    bgcolor: "rgba(124, 58, 237, 0.12)",
                    color: "#7c3aed",
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RuleOutlined fontSize="small" />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#7c3aed" }}>
                {stats.uniqueTypes} / {WALLET_APPROVAL_TYPES.length}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Wallet governance coverage
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Main SGW Approvals Table Card ───────────────────────────────── */}
      <Card
        sx={{
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${
            isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
          }`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Filters, Dropdowns & Action Buttons in One Cohesive Toolbar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", flex: 1 }}>
              <TextField
                placeholder="Search by Name, Email, Phone, Approval..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ fontSize: 18, color: colors.grey[400] }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 260,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              {/* Filter by Wallet Approval Type */}
              <TextField
                select
                size="small"
                value={filterApprovalType}
                onChange={(e) => setFilterApprovalType(e.target.value)}
                sx={{
                  minWidth: 210,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              >
                <MenuItem value="ALL">All Wallet Approvals</MenuItem>
                {WALLET_APPROVAL_TYPES.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Filter by Status (Yes / No) */}
              <TextField
                select
                size="small"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{
                  minWidth: 140,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="Yes">Yes (Authorized)</MenuItem>
                <MenuItem value="No">No (Revoked)</MenuItem>
              </TextField>

              {/* Action Buttons Directly Beside "All Statuses" Dropdown */}
              <Tooltip title="Export current approval assignments to CSV">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadOutlined />}
                  onClick={handleExportCSV}
                  disabled={!approvals.filter((a) => !a.isDeleted).length}
                  sx={{
                    borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                    color: colors.grey[100],
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 2,
                    height: 40,
                    px: 1.8,
                  }}
                >
                  Export CSV
                </Button>
              </Tooltip>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined />}
                onClick={() => {
                  setEditingItem(null);
                  setFormOpen(true);
                }}
                sx={{
                  backgroundColor: "#1766a2",
                  "&:hover": { backgroundColor: "#114c79" },
                  color: "#ffffff",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 2,
                  height: 40,
                  px: 2.2,
                  boxShadow: "0 2px 8px rgba(23, 102, 162, 0.25)",
                }}
              >
                + Assign SGW Approver
              </Button>
            </Box>

            <Tooltip title="Refresh approvals from storage">
              <IconButton size="small" onClick={loadApprovals}>
                <RefreshOutlined />
              </IconButton>
            </Tooltip>
          </Box>

          {/* DataGrid with Horizontal Inscroll like Withdrawal Approvals Table */}
          <Box sx={{ width: "100%" }}>
            <DataGrid
              autoHeight
              rows={filteredApprovals}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableSelectionOnClick
              sx={gridSx}
            />
          </Box>
        </CardContent>
      </Card>

      {/* ── Action Dropdown Menu (Edit / Soft Delete) ────────────────────── */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleCloseActionMenu}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 140,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
          },
        }}
      >
        <MenuItem onClick={handleEditFromAction} sx={{ py: 1 }}>
          <ListItemIcon>
            <EditOutlined fontSize="small" sx={{ color: "#1766a2" }} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Edit
              </Typography>
            }
          />
        </MenuItem>
        <MenuItem onClick={handleSoftDeleteFromAction} sx={{ py: 1 }}>
          <ListItemIcon>
            <DeleteOutline fontSize="small" sx={{ color: "#dc2626" }} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#dc2626" }}>
                Delete
              </Typography>
            }
          />
        </MenuItem>
      </Menu>

      {/* ── SGW Approval Assignment & Edit Form Modal ────────────────────── */}
      <SgwApprovalForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSaveApproval}
        initialData={editingItem}
        isEditing={Boolean(editingItem)}
      />
    </Box>
  );
};

export default SgwApprovals;
